import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Kpis, Panel, useFlash } from '../../ui/kit.js';
import { day } from '../../state/building.js';

import Chiller from '../../media/images/modules/asset-chiller.jpg';
import Generator from '../../media/images/modules/asset-generator.jpg';
import Elevator from '../../media/images/modules/asset-elevator.jpg';
import FirePump from '../../media/images/modules/asset-fire-pump.jpg';
import PoolPump from '../../media/images/modules/asset-pool-pump.jpg';
import CoolingTower from '../../media/images/modules/asset-cooling-tower.jpg';

const FREQUENCY = { Weekly: 7, Monthly: 30, Quarterly: 90, Annual: 365 };
const STATUS_TONE = { 'Overdue': 'alert', 'Due soon': 'warn', 'Scheduled': 'info', 'Complete': 'ok' };

const ASSET_PHOTOS = {
    'Chillers 1 and 2': Chiller,
    'Emergency generator': Generator,
    'Passenger elevators A to D': Elevator,
    'Fire pump and jockey pump': FirePump,
    'Pool and spa pumps': PoolPump,
    'Cooling tower': CoolingTower
};

const ASSETS = [
    { id: 'chiller', name: 'Chillers 1 and 2', location: 'Roof mechanical', health: 72 },
    { id: 'generator', name: 'Emergency generator', location: 'P1 generator room', health: 88 },
    { id: 'elevators', name: 'Passenger elevators A to D', location: 'Core', health: 64 },
    { id: 'firepump', name: 'Fire pump and jockey pump', location: 'P1 pump room', health: 91 },
    { id: 'pool', name: 'Pool and spa pumps', location: 'Pool deck equipment room', health: 38 },
    { id: 'tower', name: 'Cooling tower', location: 'Roof', health: 55 }
];

/* Due is a day offset from today; done marks the current cycle logged. */
const SEED = [
    { id: 1, asset: 'chiller', task: 'Chiller condenser tube cleaning and refrigerant check', frequency: 'Quarterly', vendor: 'Trane Service', due: -3, done: false },
    { id: 2, asset: 'generator', task: 'Generator load bank test, 30 minutes', frequency: 'Monthly', vendor: 'In house', due: 2, done: false },
    { id: 3, asset: 'elevators', task: 'Annual elevator inspection with state inspector', frequency: 'Annual', vendor: 'Atlantic Vertical Elevator', due: 12, done: false },
    { id: 4, asset: 'firepump', task: 'Fire pump churn test and log', frequency: 'Weekly', vendor: 'In house', due: 0, done: false },
    { id: 5, asset: 'pool', task: 'Pool pump seals and strainer baskets', frequency: 'Monthly', vendor: 'Blue Wave Pools', due: -1, done: false },
    { id: 6, asset: 'tower', task: 'Cooling tower water treatment and fill inspection', frequency: 'Monthly', vendor: 'ChemAqua', due: 21, done: false },
    { id: 7, asset: 'chiller', task: 'Chiller oil analysis', frequency: 'Annual', vendor: 'Trane Service', due: 180, done: true, last: day(-4) }
];

const statusOf = item => (item.done ? 'Complete' : item.due < 0 ? 'Overdue' : item.due <= 7 ? 'Due soon' : 'Scheduled');
const meterTone = health => (health < 40 ? ' meter--alert' : health < 70 ? ' meter--warn' : '');

/* Maintenance Management: the preventive maintenance calendar for the
   building's plant, and the health of each asset it keeps running. */
function MaintenanceScreen() {
    const [schedule, setSchedule] = useState(SEED);
    const [assets, setAssets] = useState(ASSETS);
    const [flash, notify] = useFlash();

    const logCompleted = item => {
        const days = FREQUENCY[item.frequency];
        setSchedule(list => list.map(s => (s.id === item.id ? { ...s, done: true, last: day(0), due: days } : s)));
        setAssets(list => list.map(a => (a.id === item.asset ? { ...a, health: Math.min(100, a.health + 15) } : a)));
        notify(`${item.task} logged. Next due ${day(days)}.`);
    };

    const count = status => schedule.filter(s => statusOf(s) === status).length;
    const sorted = [...schedule].sort((a, b) => Number(a.done) - Number(b.done) || a.due - b.due);
    const assetName = id => assets.find(a => a.id === id)?.name;
    const avgHealth = Math.round(assets.reduce((sum, a) => sum + a.health, 0) / assets.length);

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Maintenance" title="Preventive maintenance" />

            <Kpis items={[
                { label: 'Overdue', value: count('Overdue'), tone: count('Overdue') ? 'alert' : 'ok' },
                { label: 'Due this week', value: count('Due soon'), tone: 'warn' },
                { label: 'Completed', value: count('Complete'), tone: 'ok' },
                { label: 'Plant health', value: `${avgHealth}%`, tone: 'info', note: 'Average across six systems' }
            ]} />

            {flash}

            <div className="split">
                <div className="stack">
                    <h4>Assets</h4>
                    {assets.map((asset, i) => (
                        <Panel key={asset.id} index={i}>
                            {ASSET_PHOTOS[asset.name] && (
                                <img className="panel__media" src={ASSET_PHOTOS[asset.name]} alt={asset.name} loading="lazy" />
                            )}
                            <div className="row__end" style={{ justifyContent: 'space-between' }}>
                                <h4>{asset.name}</h4>
                                <span className="row__meta">{asset.health}%</span>
                            </div>
                            <div className={`meter${meterTone(asset.health)}`}>
                                <span className="meter__fill" style={{ width: `${asset.health}%` }} />
                            </div>
                            <p className="row__meta">{asset.location}</p>
                        </Panel>
                    ))}
                </div>

                <div className="stack">
                    <h4>Schedule</h4>
                    <ul className="rows">
                        {sorted.map(item => {
                            const status = statusOf(item);
                            return (
                                <li className="row" key={item.id}>
                                    <div className="row__main">
                                        <span className="row__title">{item.task}</span>
                                        <span className="row__meta">
                                            {assetName(item.asset)} · {item.frequency} · {item.vendor} · due {day(item.due)}
                                            {item.last ? ` · last done ${item.last}` : ''}
                                        </span>
                                    </div>
                                    <div className="row__end">
                                        <Chip tone={STATUS_TONE[status]}>{status}</Chip>
                                        {!item.done && (
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={() => logCompleted(item)}>
                                                Log completed
                                            </button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default MaintenanceScreen;
